import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrder } from '../order/OrderContext';
import ApiService from '../services/ApiService';
import { getGA4SessionId } from '../utils/ga4';

const deliveryOptions = [
    { id: 'kurier', label: 'Kurier InPost', description: 'Dostawa kurierem bezpośrednio pod wskazany adres', cost: 17.0 },
    { id: 'paczkomat', label: 'Paczkomat InPost', description: 'Dostawa do wybranego automatu paczkowego InPost', cost: 15.0 },
];

const paymentOptions = [
    { id: 'transfer', label: 'Szybki przelew', description: 'Przelew przez bankowość internetową (Przelewy24)', method: null, channel: 2, iconUrl: 'https://www.przelewy24.pl/themes/przelewy24/assets/img/download/p24.svg' },
    { id: 'blik', label: 'BLIK', description: 'Płatność kodem BLIK z aplikacji bankowej', method: 154, channel: 8192, iconUrl: 'https://static.przelewy24.pl/payment-form-logo/154/img.svg'}
];
// Funkcja do ładowania skryptu InPost
const loadInPostWidget = () => {
    return new Promise((resolve, reject) => {
        if (window.easyPack) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://geowidget.inpost-group.com/inpost-geowidget.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Nie udało się załadować skryptu InPost'));
        document.head.appendChild(script);
    });
};

// Globalna funkcja callback dla InPost (musi być w window)
window.afterPointSelected = function(point) {
    const event = new CustomEvent('inpostPointSelected', {
        detail: {
            name: point.name,
            address: `${point.address.line1}${point.address.line2 ? ', ' + point.address.line2 : ''}`
        }
    });

    window.dispatchEvent(event);
};

const SummaryPage = () => {
    const { models: allModels, clearSession, leadTimeDays, showGrossPrices, setShowGrossPrices, cachedGaClientId, cachedGaSessionId, sessionId } = useOrder();

    useEffect(() => {
        document.title = "Zamówienie - Geometry Hustlers";
    }, []);
    // Filtruj tylko prawidłowe modele (bez błędów i bez przekroczonych wymiarów)
    const models = allModels.filter(model =>
    !model.error &&
    !model.hasOversizedDimension &&
    !model.priceError &&
    !model.isCalculating &&
    model.priceNet > 0 &&
    model.printTime !== undefined &&
    model.filamentUsed !== undefined &&
    model.material !== undefined
    );

    // Form state
    const [form, setForm] = useState({
        name: "",
        surname: "",
        email: "",
        phone: "",
        address: "",
        postcode: "",
        city: "",
        country: "Polska",
        company: false,
        companyName: "",
        nip: "",
        companyAddress: "",
        companyPostcode: "",
        companyCity: "",
        companyCountry: "Polska",
        shipping: "",
        payment: "",
        note: "",
        termsAccepted: false
    });

    const [userData, setUserData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedParcel, setSelectedParcel] = useState(null);
    const navigate = useNavigate();
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [allAccepted, setAllAccepted] = useState(false);

    // Funkcje obsługi checkboxów (goście):
    const handleSelectAll = (checked) => {
        setAllAccepted(checked);
        setForm(prev => ({ ...prev, termsAccepted: checked }));
        setPrivacyAccepted(checked);
        setMarketingConsent(checked);
    };

    // Przekierowanie jeśli brak modeli
    useEffect(() => {
        if (models.length === 0 && !isSubmitting) {
            navigate('/quote');
        }
    }, [models, navigate, isSubmitting]);

    // Dla synchronizacji checkboxów:
    useEffect(() => {
        const allRequired = form.termsAccepted && privacyAccepted;
        const allIncludingOptional = allRequired && marketingConsent;
        setAllAccepted(allIncludingOptional);
    }, [form.termsAccepted, privacyAccepted, marketingConsent]);


    // Dodaj nowy useEffect dla InPost
    useEffect(() => {
        const handleInpostSelection = (event) => {

            setSelectedParcel({
                name: event.detail.name,
                address: event.detail.address
            });

            setForm(prev => ({
                ...prev,
                shipping: 'paczkomat'
            }));

            // Zamknij modal
            document.getElementById('parcel-modal').style.display = 'none';
        };

        window.addEventListener('inpostPointSelected', handleInpostSelection);

        return () => {
            window.removeEventListener('inpostPointSelected', handleInpostSelection);
        };
    }, []);



    // Pobierz dane adresowe, jeśli user zalogowany
    useEffect(() => {
        (async () => {
            setLoading(true);
            const response = await ApiService.getUserData();
            const data = response?.userData;

            setUserData(data);

            // Jeśli mamy dane użytkownika, wypełnij formularz
            if (data) {
                setForm(prev => ({
                    ...prev,
                    name: data.name || "",
                    surname: data.surname || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    address: data.address || "",
                    postcode: data.postcode || "",
                    city: data.city || "",
                    country: data.country || "Polska",
                    companyName: data.company_name || "",
                    nip: data.company_nip || "",
                    companyAddress: data.company_address || "",
                    companyPostcode: data.company_postcode || "",
                    companyCity: data.company_city || "",
                    companyCountry: data.company_country || "Polska",
                    company: !!(data.company_name && data.company_nip), // checkbox zaznaczony jeśli są dane firmy
                    termsAccepted: true
                }));
            }
            setLoading(false);
        })();
    }, []);


    // Sprawdź czy użytkownik ma kompletne dane do wysyłki
    const hasShippingData = userData && userData.name && userData.surname &&
    userData.address && userData.postcode && userData.city &&
    userData.phone;

    // Sprawdź czy użytkownik ma dane firmowe
    const hasCompanyData = userData && userData.company_name && userData.company_nip &&
    userData.company_address && userData.company_postcode &&
    userData.company_city;

    // Obsługa zmiany formularza
    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNipChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Tylko cyfry
        if (value.length <= 10) {
            setForm(prev => ({
                ...prev,
                nip: value
            }));
        }
    };

    const handleParcelSelection = async () => {
        try {
            const modal = document.getElementById('parcel-modal');
            if (modal) modal.style.display = 'block';
            await loadInPostWidget();
        } catch (error) {
            alert('Błąd ładowania mapy paczkomatów: ' + error.message);
        }
    };

    // Nowa funkcja obsługi wyboru punktu
    const handlePointSelected = (event) => {
        const point = event.detail;
        setSelectedParcel({
            name: point.name,
            address: `${point.address_details?.street || ''} ${point.address_details?.building_number || ''}, ${point.address_details?.city || ''}`
        });

        setForm(prev => ({
            ...prev,
            shipping: 'paczkomat'
        }));

        // Zamknij modal
        document.getElementById('parcel-modal').style.display = 'none';
    };

    const VAT_RATE = 0.23;

    // Wyliczenia części (backend już policzył VAT dla każdego modelu)
    const partsNetRaw = +models.reduce((sum, m) => sum + (m.priceNet * m.quantity), 0).toFixed(2);
    const partsVatRaw = +models.reduce((sum, m) => sum + (m.vat * m.quantity), 0).toFixed(2);
    const partsGrossRaw = +models.reduce((sum, m) => sum + (m.priceGross * m.quantity), 0).toFixed(2);

    // Minimalna wartość 25 zł NETTO
    const minimumOrder = 25;
    const isMinimumApplied = partsNetRaw < minimumOrder;
    const partsNet = Math.max(partsNetRaw, minimumOrder);

    const partsVat = isMinimumApplied
    ? Number((partsNet * VAT_RATE).toFixed(2))
    : partsVatRaw;
    const partsGross = +(partsNet + partsVat).toFixed(2);

    // Dostawa
    const shippingNet = deliveryOptions.find(o => o.id === form.shipping)?.cost || 0;
    const shippingVat = shippingNet > 0 ? Number((shippingNet * VAT_RATE).toFixed(2)) : 0;
    const shippingGross = +(shippingNet + shippingVat).toFixed(2);

    // Suma końcowa
    const totalNet = +(partsNet + shippingNet).toFixed(2);
    const totalVat = +(partsVat + shippingVat).toFixed(2);
    const totalGross = +(totalNet + totalVat).toFixed(2);

    // Funkcja sprawdzająca czy formularz jest kompletny
    const isFormValid = () => {
        const basicDataValid = form.name && form.surname && form.email &&
        form.phone && form.address && form.postcode && form.city;

        const companyDataValid = !form.company ||
        (form.companyName && form.nip && form.companyAddress &&
        form.companyPostcode && form.companyCity);

        const consentsValid = userData || (form.termsAccepted && privacyAccepted);
        const paczkomatValid = form.shipping !== 'paczkomat' || selectedParcel;
        const shippingSelected = form.shipping !== '';
        const paymentSelected = form.payment !== '';

        return basicDataValid && companyDataValid && consentsValid && paczkomatValid && shippingSelected && paymentSelected;
    };





    // Submit handler
    const handleSubmit = async e => {
        e.preventDefault();

        if (!userData && (!form.termsAccepted || !privacyAccepted)) {
            return alert("Musisz zaakceptować regulamin i politykę prywatności!");
        }

        setIsSubmitting(true);

        try {
            // Pobierz GA4 Session ID z fallbackiem (identycznie jak w createNewSession)
            let gaSessionId = cachedGaSessionId;
            if (!gaSessionId) {
                gaSessionId = await getGA4SessionId();
            }

            const order = {
                models: models.map(m => ({
                    ...m,
                    totalNet: Number((m.priceNet * m.quantity).toFixed(2)),
                    totalGross: Number((m.priceGross * m.quantity).toFixed(2)),
                    totalVat: Number((m.vat * m.quantity).toFixed(2))
                })),
                client: {
                    name: form.name,
                    surname: form.surname,
                    email: form.email,
                    phone: form.phone,
                    address: form.address,
                    postcode: form.postcode,
                    city: form.city,
                    country: form.country,
                    company: form.company,
                    company_name: form.company ? form.companyName : undefined,
                    company_nip: form.company ? form.nip : undefined,
                    company_address: form.company ? form.companyAddress : undefined,
                    company_postcode: form.company ? form.companyPostcode : undefined,
                    company_city: form.company ? form.companyCity : undefined,
                    company_country: form.company ? form.companyCountry : undefined,
                },
                shipping: {
                    method: form.shipping,
                    shippingNet,
                    shippingGross,
                    shippingVat,
                    shippingVatRate: 23,
                    ...(form.shipping === 'paczkomat' && selectedParcel && {
                        paczkomat: selectedParcel.name,
                        paczkomatAddress: selectedParcel.address
                    })
                },
                payment: {
                    method: form.payment,
                    p24Method: paymentOptions.find(opt => opt.id === form.payment)?.method || null,
                    p24Channel: paymentOptions.find(opt => opt.id === form.payment)?.channel || null
                },
                pricing: {
                    partsNet,
                    partsGross,
                    partsVat,
                    shippingNet,
                    shippingGross,
                    shippingVat,
                    totalNet,
                    totalGross,
                    totalVat,
                    vatRate: 23
                },
                total: partsNet,
                note: form.note || "",
                leadTimeDays: leadTimeDays,
                ga_client_id: cachedGaClientId,
                ga_session_id: gaSessionId,
                sessionId: sessionId,
                consents: !userData ? {
                    termsAccepted: form.termsAccepted,
                    privacyAccepted: privacyAccepted,
                    marketingConsent: marketingConsent
                } : null
            };
            // KROK 1: Walidacja
            const validation = await ApiService.validateOrder(order);

            if (!validation.valid) {
                setIsSubmitting(false);
                return alert('Kwoty zamówienia nie zgadzają się. Odśwież stronę i spróbuj ponownie.');
            }

            // KROK 2: Zapisz zamówienie
            const resp = await ApiService.submitOrder(order);
            const orderId = resp.orderId;

            // KROK 3: Inicjuj płatność
            const paymentResp = await ApiService.initPayment(orderId);

            // KROK 4: Zapisz orderId do localStorage
            localStorage.setItem('pending_order_id', orderId);

            // KROK 5: Wyczyść sesję
            await clearSession('ordered');

            // KROK 6: Przekieruj na P24
            window.location.href = paymentResp.paymentUrl;

        } catch (err) {
            setIsSubmitting(false);
            alert('Błąd zamówienia: ' + (err.message || err));
        }
    };


    return (
        <div className="app">

        <form onSubmit={handleSubmit}>
        <div className="summary-layout">
        <div className="summary-content">
        <button type="button" className="btn" onClick={() => navigate('/quote')}>← Wróć do konfiguracji</button>

        {/*PODSUMOWANIE - MODELE 3D*/}
        <h3>Twoje części</h3>
        {models.map(model => (
            <div key={model.id} className="order-item">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${model.thumbnailUrl}`} alt={model.name} className="order-item-image" />
            <div className="order-item-details">
            <div className="order-item-name">{model.name}</div>
            <div className="order-item-specs">
            {model.technology}, {model.material}, {model.color}, {model.infill}%<br />
            Ilość: {model.quantity} szt. – {(showGrossPrices ? model.priceGross : model.priceNet).toFixed(2)} PLN/szt
            </div>
            </div>
            <div className="order-item-price">{((showGrossPrices ? model.priceGross : model.priceNet) * model.quantity).toFixed(2)} PLN</div>
            </div>
        ))}
        {/*PODSUMOWANIE - MODELE 3D*/}

        <div className="text-small" style={{ color: '#888', marginTop: '10px', textAlign: 'right' }}>
        {showGrossPrices
            ? "Ceny zawierają VAT 23%"
            : "Ceny nie uwzględniają podatku VAT"}
            </div>

        {/* CZAS REALIZACJI POD POZYCJAMI */}
        {leadTimeDays && (
            <>
            <hr style={{
                border: 'none',
                borderTop: '2px solid #dee2e6',
                margin: '20px 0 16px 0'
            }} />
            <div style={{
                padding: '12px 0',
                textAlign: 'center'
            }}>
            {leadTimeDays > 40 ? (
                // KOMUNIKAT DLA DŁUGICH TERMINÓW
                <div>
                <div style={{
                    fontSize: '16px',
                    color: '#333',
                    fontWeight: '600',
                    marginBottom: '10px'
                }}>
                🚀 To większe zlecenie!
                </div>
                <div className="alert-warning" style={{
                    fontSize: '14px',
                    padding: '12px',
                    borderRadius: '6px',
                    lineHeight: '1.5',
                    margin: '0 auto',
                    maxWidth: '500px'
                }}>
                Czas realizacji może wynieść więcej niż 40 dni roboczych.
                </div>
                </div>
            ) : (
                // STANDARDOWY KOMUNIKAT Z DNIAMI
                <>
                <div style={{
                    fontSize: '15px',
                    color: '#333',
                    lineHeight: '1.6',
                    marginBottom: '6px'
                }}>
                Przewidywany czas realizacji: <strong>{leadTimeDays} dni rob.*</strong>
                </div>
                <div style={{
                    fontSize: '13px',
                    color: '#888',
                    fontStyle: 'italic'
                }}>
                *W przypadku zamówienia przed godziną 12:00
                </div>
                </>
            )}
            </div>
            </>
        )}
        {/* CZAS REALIZACJI POD POZYCJAMI */}

        <h3 style={{ marginTop: 32 }}>Dane do wysyłki</h3>

        {loading && (
            <div className="loading-text">Ładuję dane adresowe...</div>
        )}

        {/* Pokaż box z danymi, gdy user jest zalogowany I ma kompletne dane I nie nacisnął "ZMIEŃ DANE" */}
        {!loading && hasShippingData && !showForm && (
            <div className="shipping-data-box">
            <div className="shipping-data-section">
            <div className="shipping-data-title">Dane do wysyłki</div>
            <div>{userData.name} {userData.surname}</div>
            <div>{userData.address}</div>
            <div>{userData.postcode} {userData.city}</div>
            <div>{userData.country || 'Polska'}</div>
            <div>{userData.phone}</div>
            </div>
            <div className="shipping-data-section shipping-data-divider">
            <div className="shipping-data-title">Dane do faktury</div>
            {hasCompanyData ? (
                <>
                <div>{userData.company_name}</div>
                <div>{userData.company_address}</div>
                <div>{userData.company_postcode} {userData.company_city}</div>
                <div>{userData.company_country || 'Polska'}</div>
                <div>NIP: {userData.company_nip}</div>
                </>
            ) : (
                <div className="shipping-data-empty">brak danych firmy</div>
            )}
            </div>
            <div>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(true)}>
            ZMIEŃ DANE
            </button>
            </div>
            </div>
        )}

        {/* Jeśli nie ma danych (user niezalogowany lub nieuzupełnione) lub zaznaczono ZMIEŃ DANE - pokaż formularz */}
        {!loading && (!hasShippingData || showForm) && (
            <>
            <div className="form-row">
            <input name="name" type="text" value={form.name} onChange={handleChange} placeholder="Imię" required className="form-input" />
            <input name="surname" type="text" value={form.surname} onChange={handleChange} placeholder="Nazwisko" required className="form-input" />
            </div>
            <div className="form-row">
            <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="E-mail" required className="form-input" />
            <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Telefon" required className="form-input" />
            </div>
            <input name="address" type="text" value={form.address} onChange={handleChange} placeholder="Ulica, nr domu/mieszkania" required className="form-input" />
            <div className="form-row">
            <input name="postcode" type="text" value={form.postcode} onChange={handleChange} placeholder="Kod pocztowy" required className="form-input" />
            <input name="city" type="text" value={form.city} onChange={handleChange} placeholder="Miasto" required className="form-input" />
            </div>
            <input name="country" type="text" value={form.country} disabled className="form-input" style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }} />

            {/*DANE FIRMY*/}
            <label className="form-label" style={{ marginTop: 10, marginBottom: 6 }}>
            <input type="checkbox" name="company" checked={form.company} onChange={handleChange} className="form-checkbox" />
            Chcę fakturę na firmę
            </label>
            {form.company && (
                <div className="form-company-section">
                <input name="companyName" type="text" value={form.companyName} onChange={handleChange} placeholder="Nazwa firmy" required={form.company} className="form-input" />
                <input name="nip" type="text" value={form.nip} onChange={handleNipChange} placeholder="NIP (10 cyfr)" required={form.company} className="form-input" pattern="[0-9]{10}" maxLength={10} inputMode="numeric" />
                <input name="companyAddress" type="text" value={form.companyAddress} onChange={handleChange} placeholder="Ulica, nr domu/mieszkania" required={form.company} className="form-input" />
                <div className="form-row">
                <input name="companyPostcode" type="text" value={form.companyPostcode} onChange={handleChange} placeholder="Kod pocztowy" required={form.company} className="form-input" />
                <input name="companyCity" type="text" value={form.companyCity} onChange={handleChange} placeholder="Miasto" required={form.company} className="form-input" />
                </div>
                <input name="companyCountry" type="text" value={form.companyCountry} disabled className="form-input" style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }} />
                </div>
            )}
            </>
        )}

        {/*UWAGI DO ZAMÓWIENIA*/}
        <textarea name="note" value={form.note} onChange={handleChange} placeholder="Uwagi do zamówienia (opcjonalnie)" className="form-textarea" />

        {/*DOSTAWA*/}
        <h3>Dostawa</h3>
        <div className="text-small" style={{ color: '#888', marginTop: -10, marginBottom: 16 }}>
        {showGrossPrices
            ? "Ceny zawierają VAT 23%"
            : "Ceny nie uwzględniają podatku VAT"}
            </div>
            <div className="delivery-payment-box">
        {deliveryOptions.map(opt => (
            <label key={opt.id} className="delivery-payment-option">
            <input
            type="radio"
            name="shipping"
            value={opt.id}
            checked={form.shipping === opt.id}
            onChange={handleChange}
            className="form-checkbox"
            />
            <div className="delivery-payment-content">

            <div className="delivery-payment-info">
            <div className="delivery-payment-title">{opt.label}</div>
            <div className="delivery-payment-description">{opt.description}</div>



            {opt.id === 'paczkomat' && form.shipping === 'paczkomat' && !selectedParcel && (
                <button
                type="button"
                className="btn btn-secondary"
                style={{ marginTop: '8px', fontSize: '12px', padding: '4px 8px' }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleParcelSelection();
                }}
                >
                Wybierz paczkomat
                </button>
            )}
            {opt.id === 'paczkomat' && form.shipping === 'paczkomat' && selectedParcel && (
                <div style={{
                    marginTop: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px'
                }}>
                <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: '#333', fontWeight: 'bold' }}>
                {selectedParcel.name}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                {selectedParcel.address}
                </div>
                </div>
                <button
                type="button"
                className="btn btn-secondary"
                style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleParcelSelection();
                }}
                >
                Zmień paczkomat
                </button>
                </div>
            )}

            </div>

            <div className="delivery-payment-price">
            {opt.cost === 0 ? "0,00 zł" : showGrossPrices
                ? `${(opt.cost * 1.23).toFixed(2).replace('.', ',')} zł`
                : `${opt.cost.toFixed(2).replace('.', ',')} zł`}
                </div>

            </div>
            </label>
        ))}
        </div>

        {/*PŁATNOŚĆ*/}
        <h3>Płatność</h3>
        <div className="delivery-payment-box">
        {paymentOptions.map(opt => (
            <label key={opt.id} className="delivery-payment-option">
            <input
            type="radio"
            name="payment"
            value={opt.id}
            checked={form.payment === opt.id}
            onChange={handleChange}
            className="form-checkbox"
            />
            <div className="delivery-payment-content">
            <img src={opt.iconUrl} alt={opt.label} style={{ width: 32, height: 32, marginRight: 12, verticalAlign: 'middle' }}/>
            <div className="delivery-payment-info">
            <div className="delivery-payment-title">{opt.label}</div>
            <div className="delivery-payment-description">{opt.description}</div>
            </div>
            </div>
            </label>
        ))}
        </div>

        </div>

        {/*PRAWA STRONA Z PODSUMOWANIEM CAŁOŚCI*/}
        <div className="summary-sidebar">
        <h3 style={{ marginTop: 0 }}>Podsumowanie</h3>




        <div style={{ marginBottom: 8 }}>
        <div className="summary-row">
        <span>Wartość części{!showGrossPrices && ' (netto)'}:</span>
        <span>{showGrossPrices ? partsGross.toFixed(2) : partsNet.toFixed(2)} PLN</span>
        </div>
        {isMinimumApplied && (
            <div className="alert-warning" style={{
                fontSize: '13px',
                padding: '10px',
                borderRadius: '6px',
                lineHeight: '1.5',
                marginTop: '10px',
                marginBottom: '10px',
                textAlign: 'center'
            }}>
            <strong>Minimalna wartość zamówienia: {showGrossPrices ? '30.75 PLN brutto' : '25.00 PLN netto'}</strong>
            </div>
        )}
        <div className="summary-row">
        <span>Dostawa{!showGrossPrices && ' (netto)'}</span>
        <span>{form.shipping === '' ? "proszę wybrać" : shippingNet === 0 ? "za darmo" : showGrossPrices
            ? `${shippingGross.toFixed(2)} PLN`
            : `${shippingNet.toFixed(2)} PLN`}</span>
            </div>
            <div className="summary-row">
            <span>{showGrossPrices ? 'w tym VAT:' : 'Suma VAT:'}</span>
            <span>{totalVat.toFixed(2)} PLN</span>
            </div>
            </div>

            <hr />
            <div className="summary-total">
            <span>Do zapłaty:</span>
            <span>{totalGross.toFixed(2)} PLN</span>
            </div>



        <div style={{ marginTop: 10, marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
        <input
        type="checkbox"
        checked={showGrossPrices}
        onChange={(e) => setShowGrossPrices(e.target.checked)}
        />
        <span className="text-small" style={{ color: '#666' }}>
        Wyświetlaj ceny brutto
        </span>
        </label>
        <div className="text-small" style={{ color: '#888', textAlign: 'left' }}>
        {showGrossPrices
            ? "Ceny zawierają VAT 23%"
            : "Ceny nie zawierają podatku VAT"}
            </div>
            </div>




        {/* Zgody - tylko dla gości (brak userData) */}
        {!userData && (
            <div className="consent-section">
            <label className="consent-label consent-label-master">
            <input
            type="checkbox"
            className="consent-checkbox consent-checkbox-master"
            checked={allAccepted}
            onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <span className="consent-text">Zaznacz wszystkie</span>
            </label>

            <label className="consent-label consent-label-required">
            <input
            type="checkbox"
            className="consent-checkbox"
            checked={form.termsAccepted}
            onChange={handleChange}
            name="termsAccepted"
            required
            />
            <span className="consent-text">
            <span className="consent-required-star">*</span>
            Akceptuję <a href="/regulamin" target="_blank" className="consent-link">Regulamin</a> (Ogólne Warunki Użytkowania oraz Ogólne Warunki Sprzedaży)
            </span>
            </label>

            <label className="consent-label consent-label-required">
            <input
            type="checkbox"
            className="consent-checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            required
            />
            <span className="consent-text">
            <span className="consent-required-star">*</span>
            Akceptuję <a href="/polityka-prywatnosci" target="_blank" className="consent-link">Politykę Prywatności i Wykorzystania Plików Cookies</a>
            </span>
            </label>

            <label className="consent-label consent-label-optional">
            <input
            type="checkbox"
            className="consent-checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            />
            <span className="consent-text">
            Wyrażam zgodę na otrzymywanie informacji marketingowych
            </span>
            </label>

            <p className="consent-note">* pola obowiązkowe</p>
            </div>
        )}

        {/* Dla zalogowanych - tylko podstawowa informacja */}
        {userData && (
            <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
            Składając zamówienie akceptujesz <a href="/regulamin" target="_blank" style={{ color: '#F53447' }}>Regulamin</a> (Ogólne Warunki Użytkowania oraz Ogólne Warunki Sprzedaży) oraz <a href="/polityka-prywatnosci" target="_blank" style={{ color: '#F53447' }}>Politykę Prywatności i Wykorzystania Plików Cookies</a>.
            </p>
        )}

        <button
        type="submit"
        className="order-submit-btn"
        disabled={!isFormValid() || isSubmitting}
        style={{
            opacity: !isFormValid() || isSubmitting ? 0.6 : 1,
            cursor: !isFormValid() ? 'not-allowed' : isSubmitting ? 'wait' : 'pointer'
        }}
        >
        {isSubmitting ? 'Przetwarzanie...' : 'Przejdź do płatności'}
        </button>

        </div>
        </div>
        </form>

        {/* Modal z mapą paczkomatów */}
        <div
        id="parcel-modal"
        style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000
        }}
        onClick={(e) => {
            if (e.target.id === 'parcel-modal') {
                e.target.style.display = 'none';
            }
        }}
        >
        <div style={{
            position: 'relative',
            margin: '5% auto',
            width: '90%',
            maxWidth: '800px',
            height: '80%',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden'
        }}>
        <button
        onClick={() => document.getElementById('parcel-modal').style.display = 'none'}
        style={{
            position: 'absolute',
            top: '10px',
            right: '15px',
            zIndex: 1001,
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer'
        }}
        >
        ✕
        </button>
        <inpost-geowidget
        id="geowidget"
        onpoint="afterPointSelected"
        token={process.env.REACT_APP_INPOST_TOKEN}
        country="PL"
        language="pl"
        config="parcelcollect"
        style={{ width: '100%', height: '100%' }}
        ></inpost-geowidget>
        </div>
        </div>
        </div>
    );
};

export default SummaryPage;
